import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductListTabsRefs
 * @property {HTMLElement[]} [tabs] - The tab buttons, in source order
 * @property {HTMLElement[]} [panels] - The rails, one per tab, in the same order
 */

/**
 * Switches a product list between its tabs.
 *
 * Every tab's rail is server-rendered, so the panels are already in the document
 * and swapping one in is a `hidden` toggle rather than a fetch. With JavaScript
 * off the first rail stays visible and the rest stay hidden, which is the same
 * thing a visitor would see before choosing a tab.
 *
 * @extends {Component<ProductListTabsRefs>}
 */
export class ProductListTabs extends Component {
  requiredRefs = ['tabs'];

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    // The typed heading may not match the first tab's collection title, so align it up front
    const selected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#syncHeading(tabs[selected === -1 ? 0 : selected]?.textContent?.trim() ?? '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const tab = target?.closest('[role="tab"]');
    if (!tab) return;

    const index = this.refs.tabs?.indexOf(/** @type {HTMLElement} */ (tab)) ?? -1;
    if (index === -1) return;

    this.#activate(index);
  };

  /** Roving focus across the strip, per the ARIA tabs pattern. @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const from = tabs.indexOf(target);
    if (from === -1) return;

    /** @type {Record<string, number>} */
    const steps = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -from,
      End: tabs.length - 1 - from,
    };

    const step = steps[event.key];
    if (step === undefined) return;

    event.preventDefault();

    const next = (from + step + tabs.length) % tabs.length;
    this.#activate(next);
    tabs[next]?.focus();
  };

  /**
   * Public so a future filter or deep link can drive the strip.
   * @param {number} index
   */
  select(index) {
    this.#activate(index);
  }

  /**
   * The section heading, when the strip is set to follow the selected tab.
   * Re-queried per switch so it survives a Section Rendering morph.
   */
  #headingTarget() {
    if (!this.querySelector('.product-list-tabs__list[data-sync-heading]')) return null;
    return this.querySelector('.section-resource-list__header :is(h1, h2, h3, h4, h5, h6)');
  }

  /** @param {string} label */
  #syncHeading(label) {
    const heading = this.#headingTarget();
    if (!heading) return;

    // The heading as rendered decides the treatment: a gold closing word, a word reveal, or neither
    const accented = heading.querySelector('.text-block__accent') != null;
    const split = heading.querySelector('.reveal-word') != null;
    const words = label.split(/\s+/).filter(Boolean);
    if (!words.length) return;

    const out = document.createDocumentFragment();

    words.forEach((word, index) => {
      if (index) out.append(' ');

      const isAccent = accented && index === words.length - 1;
      if (!isAccent && !split) {
        out.append(word);
        return;
      }

      const span = document.createElement('span');
      if (isAccent) span.classList.add('text-block__accent');
      if (split) {
        span.classList.add('reveal-word');
        span.style.setProperty('--reveal-index', String(index));
      }
      span.textContent = word;
      out.append(span);
    });

    heading.replaceChildren(out);
  }

  /** @param {number} index */
  #activate(index) {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || index < 0 || index >= tabs.length) return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      panel.toggleAttribute('hidden', i !== index);
    }

    this.#syncHeading(tabs[index]?.textContent?.trim() ?? '');
  }
}

if (!customElements.get('product-list-tabs')) {
  customElements.define('product-list-tabs', ProductListTabs);
}
